import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { CancelSharp, CheckCircleSharp } from '@mui/icons-material'
import { parseISO } from 'date-fns'
import type { JSX, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { RDI_3000 } from '@/pages/Device/SupportedTypes'
import { CompositionCheckRow } from './CompositionCheckRow'
import type {
  Composition,
  DeviceCompositionKey,
  IccidStatus,
} from '@/pages/calibration/composition'

const SUCCESS_COLOR = '#4caf50'

export const compareVersions = (version1: string, version2: string): number => {
  const v1parts = version1.split('.').map(Number)
  const v2parts = version2.split('.').map(Number)
  const length = Math.max(v1parts.length, v2parts.length)
  for (let i = 0; i < length; i++) {
    const v1part = v1parts[i] ?? 0
    const v2part = v2parts[i] ?? 0
    if (v1part > v2part) return 1
    if (v1part < v2part) return -1
  }
  return 0
}

export const isVersionGreaterThanOrEqual = (
  version: string,
  targetVersion: string,
): boolean => compareVersions(version, targetVersion) >= 0

const ICCID_PREFIX_VALID_REASON =
  'Device matches eligible configuration. ICCID starts with 893324'
const ICCID_PREFIX_INVALID_REASON =
  'Device matches eligible configuration. SIM card needs to be replaced. ICCID does not start with 893324'

const isIccidPrefixRelevant = (iccidStatus: IccidStatus): boolean =>
  iccidStatus.reasons?.some(
    (r) =>
      r.includes(ICCID_PREFIX_VALID_REASON) ||
      r.includes(ICCID_PREFIX_INVALID_REASON),
  ) ?? false

const shouldShowRdpRow = (composition: Composition): boolean => {
  if (!composition.ncmType || !composition.configuration?.ncmFirmware) {
    return false
  }
  const isNmdi = composition.ncmType === 'nmdi'
  const configNcmFirmwareVersions = composition.configuration.ncmFirmware
  const hasValidConfigVersion =
    Array.isArray(configNcmFirmwareVersions) &&
    configNcmFirmwareVersions.some((version) =>
      isVersionGreaterThanOrEqual(version, '2.1.0'),
    )
  return isNmdi && hasValidConfigVersion
}

interface Props {
  composition: Composition | null
  caption?: ReactNode
  updatedAtPrefix?: ReactNode
}

// Shared header style — no whiteSpace:nowrap so cells can wrap when zoomed
const headerCellSx = {
  fontWeight: 'bold',
  fontSize: '0.875rem',
  color: 'rgba(0,0,0,0.87)',
  borderBottom: '2px solid rgba(0,0,0,0.12)',
  py: 1.5,
  verticalAlign: 'top',
}

const CompositionCheckTable = ({
  composition,
  caption,
  updatedAtPrefix,
}: Props): JSX.Element => {
  const { t } = useTranslation('app')

  const updatedAtLabel = composition
    ? t('device.calibration.preFlightStep.updatedAt', {
        date: parseISO(composition.composition.updatedAt).toLocaleString(),
        interpolation: { escapeValue: false },
      })
    : t('common:general.notAvailable')

  return (
    <Box>
      <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
        <Table sx={{ tableLayout: 'fixed', minWidth: 600, width: '100%' }}>
          <colgroup>
            <col style={{ width: '35%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '27.5%' }} />
            <col style={{ width: '27.5%' }} />
          </colgroup>

          <TableHead>
            <TableRow>
              <TableCell component="th" sx={headerCellSx}>
                <Box
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}
                >
                  {updatedAtPrefix}
                  <Box
                    component="span"
                    sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                  >
                    {updatedAtLabel}
                  </Box>
                </Box>
              </TableCell>

              <TableCell
                component="th"
                align="right"
                sx={{ ...headerCellSx, whiteSpace: 'nowrap' }}
              >
                {t('device.calibration.preFlightStep.valid')}
              </TableCell>

              <TableCell
                component="th"
                align="right"
                sx={{ ...headerCellSx, wordBreak: 'break-word' }}
              >
                {t('device.calibration.preFlightStep.actualValue')}
              </TableCell>

              <TableCell
                component="th"
                align="right"
                sx={{ ...headerCellSx, wordBreak: 'break-word' }}
              >
                {t('device.calibration.preFlightStep.allowedValues')}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {composition && (
              <>
                {composition.configuration.type !== RDI_3000 && (
                  <TableRow>
                    <TableCell component="th" scope="row">
                      {t('device.calibration.preFlightStep.check.SIMCardICCID')}
                    </TableCell>
                    <TableCell align="right">
                      {composition.mismatches.includes('iccid') ? (
                        <CancelSharp color="error" aria-label="invalid-sim" />
                      ) : (
                        <CheckCircleSharp
                          sx={{ color: SUCCESS_COLOR }}
                          aria-label="valid-sim"
                        />
                      )}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        wordBreak: 'break-all',
                        overflowWrap: 'break-word',
                      }}
                    >
                      {composition.iccidStatus.iccid ||
                        t(
                          'device.calibration.preFlightStep.check.NoICCIDFound',
                        )}
                    </TableCell>
                    <TableCell align="right">
                      {isIccidPrefixRelevant(composition.iccidStatus)
                        ? t(
                            'device.calibration.preFlightStep.check.actualCheckForFaultSIM',
                          )
                        : t(
                            'device.calibration.preFlightStep.check.newSimMessage',
                          )}
                    </TableCell>
                  </TableRow>
                )}

                <CompositionCheckRow
                  valueKey="ncmHardware"
                  composition={composition}
                />
                <CompositionCheckRow
                  valueKey="ncmFirmware"
                  composition={composition}
                />
                <CompositionCheckRow
                  valueKey="mainHardware"
                  composition={composition}
                />
                <CompositionCheckRow
                  valueKey="mainFirmware"
                  composition={composition}
                />

                <CompositionCheckRow
                  valueKey="modemFirmware"
                  composition={composition}
                  compositionCellProps={{
                    sx: { wordBreak: 'break-all', overflowWrap: 'break-word' },
                  }}
                  configurationCellProps={{
                    sx: { wordBreak: 'break-all', overflowWrap: 'break-word' },
                  }}
                />
                <CompositionCheckRow
                  valueKey="switchHardware"
                  composition={composition}
                />
                <CompositionCheckRow
                  valueKey="switchFirmwareControl"
                  composition={composition}
                />
                <CompositionCheckRow
                  valueKey="switchFirmwareDrive"
                  composition={composition}
                />
                <CompositionCheckRow
                  valueKey="switchFirmwareMeasure"
                  composition={composition}
                />

                {shouldShowRdpRow(composition) &&
                  (() => {
                    const configRdpNmdi = composition.configuration.rdpNmdi
                    const isConfigEmpty =
                      !Array.isArray(configRdpNmdi) ||
                      configRdpNmdi.length === 0

                    const safeComposition: Composition = {
                      ...composition,
                      mismatches: isConfigEmpty
                        ? ([
                            ...new Set([...composition.mismatches, 'rdpNmdi']),
                          ] as DeviceCompositionKey[])
                        : composition.mismatches,
                    }

                    return (
                      <CompositionCheckRow
                        valueKey="rdpNmdi"
                        composition={safeComposition}
                        configurationRenderer={
                          isConfigEmpty ? (
                            <div>
                              {t(
                                'device.calibration.preFlightStep.check.notConfigured',
                              )}
                            </div>
                          ) : (
                            <>
                              {composition.configuration.rdpNmdi.map(
                                (value) => (
                                  <div key={value}>{value}</div>
                                ),
                              )}
                            </>
                          )
                        }
                      />
                    )
                  })()}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {caption && <Box sx={{ mt: 1, px: 1 }}>{caption}</Box>}
    </Box>
  )
}

export default CompositionCheckTable
